import { CONTRACT_ROUTES } from '@views/ContractPage/contractPage.constants';
import { LOGIN_ROUTES } from '@views/login/login.constants';
import { MESSAGES_ROUTES } from '@views/MessagesPage/messages.constants';
import { PARKING_ROUTES } from '@views/parking/parking.constants';
import { SAUNA_ROUTES } from '@views/sauna/sauna.constants';

/** Page routes that are defined in Contentful and, hence, their pathname
 * depends on Contentful and not the code and has to be kept up-to-date
 * manually in the code */
export const FIXED_PATHS = {
  FRONTPAGE: '/',
  MY_HOME: '/my-home',
  REAL_ESTATE_AND_COMMON_SPACES: '/my-home/real-estate-and-common-spaces',
  WATER_USAGE: '/my-home/apartment/water-usage',
  BROADBAND: '/my-home/apartment/broadband',
  TEMPERATURE: '/my-home/apartment/temperature',
  BENEFITS: '/my-home/benefits',
  MARKETING_PERMISSION: '/my-information/marketing-permission',
  PARKING_INSTRUCTIONS_WHEN_RENTED_ONLINE:
    '/my-home/parking/instructions-omasato',
  PARKING_INSTRUCTIONS_WHEN_RENTED_BY_CONTACTING_SUPPORT:
    '/my-home/parking/instructions-sato',
  PARKING_INSTRUCTIONS_WHEN_RENTED_BY_MAINTENANCE_COMPANY:
    '/my-home/parking/instructions-maintenance-company',
  HOME_SWAPPERS: '/my-home/for-home-swappers',
  USERS_CONTRACT_HAS_ENDED: 'contract-has-ended',
} as const;

//TODO: Separate routes under myhome and others with subroutes to clarify
export const ROUTES = {
  ...LOGIN_ROUTES,
  ...FIXED_PATHS,
  ...SAUNA_ROUTES,
  ...MESSAGES_ROUTES,
  ...CONTRACT_ROUTES,
  ...PARKING_ROUTES,
  APARTMENT: '/my-home/apartment',
  REAL_ESTATE_AND_COMMON_SPACES: '/my-home/real-estate-and-common-spaces',

  CLUB_HOUSE_RESERVATION: '/my-home/club-room-reservation/:id?',

  // === MY_INFORMATION PAGES ===
  CHANGE_PASSWORD: '/my-information/new-password',
  MY_INFORMATION: '/my-information',

  REDIRECT: '/redirect',

  FTN_AUTHENTICATION: '/authentication/:purpose',
} as const;

export enum SatoFIRoute {
  URL_TIPS_PAGE = 'URL_TIPS_PAGE',
  URL_TIPS_SUB_PAGE = 'URL_TIPS_SUB_PAGE',
  URL_FAQ_PAGE = 'URL_FAQ_PAGE',
  URL_FAQ_SUB_PAGE = 'URL_FAQ_SUB_PAGE',
  URL_APARTMENT_ON_DRAWING_TABLE = 'URL_APARTMENT_ON_DRAWING_TABLE',
  URL_APARTMENT_PAGE = 'URL_APARTMENT_PAGE',
  URL_REAL_ESTATE_PAGE = 'URL_REAL_ESTATE_PAGE',
  URL_APARTMENT_SEARCH = 'URL_APARTMENT_SEARCH',
  URL_APARTMENT_MARKETING_PAGE = 'URL_APARTMENT_MARKETING_PAGE',
  URL_HOME_APPLICATION = 'URL_HOME_APPLICATION',
  URL_HOME_APPLICATION_FRONT_PAGE = 'URL_HOME_APPLICATION_FRONT_PAGE',
  URL_CITY_PAGE = 'URL_CITY_PAGE',
  URL_DISTRICT_PAGE = 'URL_DISTRICT_PAGE',
  URL_CONTACT_INFO_PAGE = 'URL_CONTACT_INFO_PAGE',
  URL_INVESTOR_PAGE = 'URL_INVESTOR_PAGE',
  URL_INVESTOR_SUB_PAGE = 'URL_INVESTOR_SUB_PAGE',
  URL_INVESTOR_RELEASES_PAGE = 'URL_INVESTOR_RELEASES_PAGE',
  URL_SATO_COMPANY_PAGE = 'URL_SATO_COMPANY_PAGE',
  URL_SATO_COMPANY_SUB_PAGE = 'URL_SATO_COMPANY_SUB_PAGE',
  URL_SATO_COMPANY_RELEASES_PAGE = 'URL_SATO_COMPANY_RELEASES_PAGE',
  URL_MEDIA_PAGE = 'URL_MEDIA_PAGE',
  URL_MEDIA_SUB_PAGE = 'URL_MEDIA_SUB_PAGE',
  URL_MEDIA_RELEASES_PAGE = 'URL_MEDIA_RELEASES_PAGE',
  URL_KOTONA_PAGE = 'URL_KOTONA_PAGE',
  URL_KOTONA_SUB_PAGE = 'URL_KOTONA_SUB_PAGE',
  URL_KOTONA_RELEASES_PAGE = 'URL_KOTONA_RELEASES_PAGE',
  URL_KOTONA_LANDING_PAGE = 'URL_KOTONA_LANDING_PAGE',
  URL_KOTONA_SECTION_PAGE = 'URL_KOTONA_SECTION_PAGE',
  URL_KOTONA_ARTICLE_PAGE = 'URL_KOTONA_ARTICLE_PAGE',
  URL_TERMINATION = 'URL_TERMINATION',
  URL_TERMINATION_MOQ = 'URL_TERMINATION_MOQ',
  URL_TERMINATION_THANKS = 'URL_TERMINATION_THANKS',
  URL_NOTIFICATION_PAGE = 'URL_NOTIFICATION_PAGE',
  URL_SATO_PAGE = 'URL_SATO_PAGE',
  URL_SATO_SUB_PAGE = 'URL_SATO_SUB_PAGE',
  URL_SEARCH_ALERT = 'URL_SEARCH_ALERT',
  URL_CONTENTFUL_RELEASE_PAGE = 'URL_CONTENTFUL_RELEASE_PAGE',
  URL_BASE = 'URL_BASE',
  URL_PARKING_TERMS = 'URL_PARKING_TERMS',
}

export const SATO_FI_ROUTES = {
  [SatoFIRoute.URL_TIPS_PAGE]: {
    fi: '/ohjeita-neuvoja-asukkaalle',
    en: '/instructions-tips-for-residents',
  },
  [SatoFIRoute.URL_TIPS_SUB_PAGE]: {
    fi: '/ohjeita-neuvoja-asukkaalle/:subPage',
    en: '/instructions-tips-for-residents/:subPage',
  },
  [SatoFIRoute.URL_FAQ_PAGE]: {
    fi: '/usein-kysyttya-vuokra-asunnot',
    en: '/faq-rental-apartments',
  },
  [SatoFIRoute.URL_FAQ_SUB_PAGE]: {
    fi: '/usein-kysyttya-vuokra-asunnot/:subPage',
    en: '/faq-rental-apartments/:subPage',
  },
  [SatoFIRoute.URL_APARTMENT_ON_DRAWING_TABLE]: {
    fi: '/suunnitteilla-olevat-vuokra-asunnot',
    en: '/planning-new-rental-apartments',
  },
  [SatoFIRoute.URL_APARTMENT_PAGE]: {
    fi: '/vuokra-asunnot/:municipality/:district/:realEstateStreetAddress/:realEstateId/asunto/:id',
    en: '/rental-apartments/:municipality/:district/:realEstateStreetAddress/:realEstateId/apartment/:id',
  },
  [SatoFIRoute.URL_REAL_ESTATE_PAGE]: {
    fi: '/vuokra-asunnot/:municipality/:district/:streetAddress/:id',
    en: '/rental-apartments/:municipality/:district/:streetAddress/:id',
  },
  [SatoFIRoute.URL_APARTMENT_SEARCH]: {
    fi: '/vuokra-asunnot',
    en: '/rental-apartments',
  },
  [SatoFIRoute.URL_APARTMENT_MARKETING_PAGE]: {
    fi: '/tarjous',
    en: '/tarjous',
  },
  [SatoFIRoute.URL_HOME_APPLICATION]: {
    fi: '/asuntohakemus/kirjautuminen',
    en: '/application/update',
  },
  [SatoFIRoute.URL_HOME_APPLICATION_FRONT_PAGE]: {
    fi: '/asuntohakemus',
    en: '/application',
  },
  [SatoFIRoute.URL_CITY_PAGE]: {
    fi: '/kaupunki/:cityId',
    en: '/city/:cityId',
  },
  [SatoFIRoute.URL_DISTRICT_PAGE]: {
    fi: '/kaupunki/:cityId/:districtId',
    en: '/city/:cityId/:districtId',
  },
  [SatoFIRoute.URL_CONTACT_INFO_PAGE]: {
    fi: '/yhteystiedot-ota-yhteytta',
    en: '/contact-information',
  },
  [SatoFIRoute.URL_INVESTOR_PAGE]: {
    fi: '/talous-sijoittajat',
    en: '/finances-investors',
  },
  [SatoFIRoute.URL_INVESTOR_SUB_PAGE]: {
    fi: '/talous-sijoittajat/:subPage',
    en: '/finances-investors/:subPage',
  },
  [SatoFIRoute.URL_INVESTOR_RELEASES_PAGE]: {
    fi: '/talous-sijoittajat/tiedotteet-sijoittajille/:releaseId',
    en: '/finances-investors/tiedotteet-sijoittajille/:releaseId',
  },
  [SatoFIRoute.URL_SATO_COMPANY_PAGE]: {
    fi: '/sato-yritys',
    en: '/sato-company',
  },
  [SatoFIRoute.URL_SATO_COMPANY_SUB_PAGE]: {
    fi: '/sato-yritys/:subPage',
    en: '/sato-company/:subPage',
  },
  [SatoFIRoute.URL_SATO_COMPANY_RELEASES_PAGE]: {
    fi: '/sato-yritys/tiedotteet-sato/:releaseId',
    en: '/sato-company/press-releases-sato/:releaseId',
  },
  [SatoFIRoute.URL_MEDIA_PAGE]: {
    fi: '/media',
    en: '/media',
  },
  [SatoFIRoute.URL_MEDIA_SUB_PAGE]: {
    fi: '/media/:subPage',
    en: '/media/:subPage',
  },
  [SatoFIRoute.URL_MEDIA_RELEASES_PAGE]: {
    fi: '/media/tiedotteet-medialle/:releaseId',
    en: '/media/tiedotteet-medialle/:releaseId',
  },
  [SatoFIRoute.URL_KOTONA_PAGE]: {
    fi: '/asukkaalle',
    en: '/for-resident',
  },
  [SatoFIRoute.URL_KOTONA_SUB_PAGE]: {
    fi: '/asukkaalle/:subPage',
    en: '/for-resident/:subPage',
  },
  [SatoFIRoute.URL_KOTONA_RELEASES_PAGE]: {
    fi: '/asukkaalle/asukastiedotteet/:releaseId',
    en: '/for-resident/customer-releases/:releaseId',
  },
  [SatoFIRoute.URL_KOTONA_LANDING_PAGE]: {
    fi: '/kotona',
    en: '/kotona',
  },
  [SatoFIRoute.URL_KOTONA_SECTION_PAGE]: {
    fi: '/kotona/:section',
    en: '/kotona/:section',
  },
  [SatoFIRoute.URL_KOTONA_ARTICLE_PAGE]: {
    fi: '/kotona/:section/:article/:articleId',
    en: '/kotona/:section/:article/:articleId',
  },
  [SatoFIRoute.URL_TERMINATION]: {
    fi: '/irtisanominen',
    en: '/termination',
  },
  [SatoFIRoute.URL_TERMINATION_MOQ]: {
    fi: '/irtisanominen/pomu',
    en: '/termination/moq',
  },
  [SatoFIRoute.URL_TERMINATION_THANKS]: {
    fi: '/irtisanominen/kiitos',
    en: '/termination/thankyou',
  },
  [SatoFIRoute.URL_NOTIFICATION_PAGE]: {
    fi: '/message/:type',
    en: '/message/:type',
  },
  [SatoFIRoute.URL_SATO_PAGE]: {
    fi: '/:page',
    en: '/:page',
  },
  [SatoFIRoute.URL_SATO_SUB_PAGE]: {
    fi: '/:page/:subPage',
    en: '/:page/:subPage',
  },
  [SatoFIRoute.URL_SEARCH_ALERT]: {
    fi: '/hakuvahti',
    en: '/search-alert',
  },
  [SatoFIRoute.URL_CONTENTFUL_RELEASE_PAGE]: {
    fi: '/sato-yritys/tiedotteet/:releaseId',
    en: '/sato-company/releases/:releaseId',
  },
  [SatoFIRoute.URL_BASE]: {
    fi: '',
    en: '',
  },
  [SatoFIRoute.URL_PARKING_TERMS]: {
    fi: '/tietosuoja/autopaikkavuokrauksen-ehdot',
    en: '/data-protection/terms-and-conditions-of-parking-space-lease',
  },
} as const;

export const SATO_FI_BASE_URL = process.env.SATOFI_URL;

export enum Anchors {
  PARKING_LISTING_PAGE_AVAILABLE_SPACES = 'availableSpacesList',
  PARKING_SPACE_INFO = 'parkingSpaceInfo',
}

/*
  These pages are hidden from users whose contract has ended.
  This list is used to also block the user from navigating
  to the pages by using for example bookmarks.
*/
export const BLOCKED_ROUTES_WHEN_CONTRACT_HAS_ENDED = [
  ROUTES.APARTMENT,
  'my-home/club-room-reservation',
  'my-home/real-estate-and-common-spaces',
  'messages/new/defect/*',
  'messages/new/parking/*',
  'messages/new/disturbance/*',
  'messages/new/keys/*',
  'messages/new/renovation/*',
  'messages/new/sauna/*',
];
